import { useEffect, useMemo, useState } from 'react';
import { Loader2, Sparkles } from 'lucide-react';
import api from '../../lib/api';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import {
  type BriefImportPreview,
  type BriefImportProposal,
  BRIEF_IMPORT_FIELDS,
  briefFieldChanged,
  formatBriefFieldValue,
} from '../../lib/googleWorkspace';

interface ImportBriefModalProps {
  jobId: string;
  documentId: string;
  isOpen: boolean;
  onClose: () => void;
  onApplied: () => void;
}

export function ImportBriefModal({
  jobId,
  documentId,
  isOpen,
  onClose,
  onApplied,
}: ImportBriefModalProps) {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<BriefImportPreview | null>(null);
  const [selected, setSelected] = useState<Set<keyof BriefImportProposal>>(new Set());

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      setPreview(null);
      try {
        const res = await api.post<BriefImportPreview>(
          `/google-workspace/jobs/${jobId}/import-brief/preview`,
          { documentId }
        );
        if (cancelled) return;
        setPreview(res.data);
        const changed = BRIEF_IMPORT_FIELDS.filter(({ key }) =>
          briefFieldChanged(res.data.current, res.data.proposed, key)
        ).map(({ key }) => key);
        setSelected(new Set(changed));
      } catch (err: unknown) {
        if (cancelled) return;
        const msg =
          (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
          'Failed to preview brief import';
        setError(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, jobId, documentId]);

  const changedCount = useMemo(() => {
    if (!preview) return 0;
    return BRIEF_IMPORT_FIELDS.filter(({ key }) =>
      briefFieldChanged(preview.current, preview.proposed, key)
    ).length;
  }, [preview]);

  const toggleField = (key: keyof BriefImportProposal) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleApply = async () => {
    if (!preview || selected.size === 0) return;
    setApplying(true);
    setError(null);
    try {
      const fields: BriefImportProposal = {};
      for (const key of selected) {
        const value = preview.proposed[key];
        if (value !== undefined) {
          (fields as Record<string, unknown>)[key] = value;
        }
      }
      await api.post(`/google-workspace/jobs/${jobId}/import-brief/apply`, { fields });
      onApplied();
      onClose();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to apply brief import';
      setError(msg);
    } finally {
      setApplying(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Import Google Doc into campaign brief"
      size="xl"
      footer={
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-gray-600">
            {selected.size} field{selected.size === 1 ? '' : 's'} selected
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={applying}>
              Cancel
            </Button>
            <Button
              onClick={() => void handleApply()}
              disabled={applying || loading || !preview || selected.size === 0}
            >
              {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply selected fields'}
            </Button>
          </div>
        </div>
      }
    >
      {loading && (
        <div className="flex flex-col items-center justify-center py-12 gap-3 text-gray-600">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          <p className="text-sm">Reading Google Doc and mapping fields with AI…</p>
        </div>
      )}

      {error && <p className="text-sm text-red-600 mb-4">{error}</p>}

      {preview && !loading && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Badge variant="outline" className="gap-1">
              <Sparkles className="h-3 w-3" />
              {preview.source === 'api' ? 'Google API' : preview.source === 'export' ? 'Public export' : 'Demo'}
            </Badge>
            {preview.documentTitle && (
              <span className="text-gray-600">Source: {preview.documentTitle}</span>
            )}
            <span className="text-gray-500">
              {changedCount} field{changedCount === 1 ? '' : 's'} differ from current brief
            </span>
            {preview.proposed.demo && (
              <Badge variant="secondary">Demo mode — connect Google OAuth for live import</Badge>
            )}
          </div>

          <div className="rounded-lg border overflow-hidden">
            <div className="grid grid-cols-[auto_1fr_1fr] gap-px bg-gray-200 text-xs font-medium uppercase tracking-wide">
              <div className="bg-gray-50 p-2" />
              <div className="bg-gray-50 p-2">Current</div>
              <div className="bg-gray-50 p-2">From Google Doc</div>
            </div>
            {BRIEF_IMPORT_FIELDS.map(({ key, label }) => {
              const changed = briefFieldChanged(preview.current, preview.proposed, key);
              const proposedValue = preview.proposed[key];
              if (proposedValue === undefined && !preview.current[key]) return null;

              return (
                <div
                  key={key}
                  className={`grid grid-cols-[auto_1fr_1fr] gap-px bg-gray-100 ${
                    changed ? 'bg-blue-50/50' : ''
                  }`}
                >
                  <div className="bg-white p-3 flex items-start">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={selected.has(key)}
                      disabled={!changed}
                      onChange={() => toggleField(key)}
                      aria-label={`Apply ${label}`}
                    />
                  </div>
                  <div className="bg-white p-3 text-sm">
                    <p className="font-medium text-gray-700 mb-1">{label}</p>
                    <p className="text-gray-600 whitespace-pre-wrap break-words">
                      {formatBriefFieldValue(preview.current[key])}
                    </p>
                  </div>
                  <div className="bg-white p-3 text-sm">
                    <p className="font-medium text-gray-700 mb-1 flex items-center gap-2">
                      {label}
                      {changed && (
                        <Badge variant="outline" className="text-[10px] py-0">
                          Changed
                        </Badge>
                      )}
                    </p>
                    <p className="text-gray-800 whitespace-pre-wrap break-words">
                      {formatBriefFieldValue(proposedValue)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          <details className="text-sm">
            <summary className="cursor-pointer text-gray-600 hover:text-gray-800">
              View extracted source text
            </summary>
            <pre className="mt-2 p-3 rounded bg-gray-50 text-xs whitespace-pre-wrap max-h-40 overflow-y-auto">
              {preview.sourceText}
            </pre>
          </details>
        </div>
      )}
    </Modal>
  );
}
